import React, { useEffect, useState, useMemo } from 'react';
import { Skeleton } from 'antd';
import { getString } from '@nackle/intl-tools';
import './styles.less';
import moment from 'moment';
import classNames from 'classnames';
import { localizeNumber } from '../../scripts/utils';
import { useAuthState } from '../../hooks/auth';

const UserSummary = ( { data, loading } ) => {
    const { claims } = useAuthState();

    const [ stats, setStats ] = useState();

    const name = useMemo( () => claims?.name, [ claims ] );
    const info = useMemo( () => {
        const brand = claims?.attributes?.brand;
        const { city, state, province, countryCode } = claims?.address ?? {};
        const address = [ city, state, province, countryCode ].map( i => i?.trim() ).filter( i => !!i ).splice( 0, 2 ).join( ', ' );
        // add title once we have that

        return [ brand, address ].filter( i => !!i ).join( ' | ' );
    }, [ claims ] );

    function crunchNumbers( data ) {
        if ( !data?.length > 0 ) {return;}

        function getMostRecentActivityDate( data ) {
            if ( data.type == 'course' ) {
                return data.completedAtMs;
            }
            if ( data.type == 'curriculum' ) {
                return getMostRecentActivityDate( data.courses );
            }
            return data.reduce( ( acc, curr ) => {
                const dt = getMostRecentActivityDate( curr );
                return dt > acc ? dt : acc;
            }, 0 );
        }

        const stats = {
            activityDate: getMostRecentActivityDate( data ),
            curriculumsCompleted: data.filter( d => d.type == 'curriculum' && d.progress == 100 ).length,
            curriculumsTotal: data.filter( d => d.type == 'curriculum' ).length,
            coursesCompleted: data.filter( d => d.type == 'course' && [ 'COMPLETED', 'COMPLETED_REVISED' ].includes( d.status ) ).length,
            coursesTotal: data.filter( d => d.type == 'course' ).length,
        };

        setStats( stats );
    }

    function formatNumber( num ) {
        return localizeNumber( num, claims.locale );
    }

    useEffect( () => {
        crunchNumbers( data );
    }, [ data ] );

    return (   
        <div className="user-summary">

            <h2 className='front-page-header highlight text-no'>{ getString( 'userSummary.title' ) }</h2>

            <div className='dataset'>
                { ( loading || name ) && 
                    <div className={ classNames( 'user-data', 'data-item', 'small', { loading } ) }>

                        { loading ? <Skeleton active paragraph={ { rows: 1, width: '100%' } } /> :
                            <>
                                { name && 
                                    <div className='data-row'>
                                        <h2 className="name highlight">{ name }</h2>
                                        { info &&
                                            <span className="info _mini">{ info }</span>
                                        }
                                    </div>
                                }
                                { name && stats?.activityDate > 0 && <div className="row-divider" /> }
                                { ( stats?.activityDate > 0 || loading ) && 
                                    <div className='data-row'>
                                        <span className="activity">
                                            <p>{ getString( 'userSummary.lastActivity' ) }:</p>
                                            <p className="activity-date highlight">
                                                { moment( stats.activityDate ).format( 'LL' ) }
                                            </p>
                                        </span>
                                    </div>
                                }
                            </>
                        }
                    </div> 
                }
                <div className='wrap-set'>
                    { ( loading || ( stats?.curriculumsTotal > 0 ) ) && 
                        <div className={ classNames( 'course-data', 'data-item', 'large', { loading } ) }>

                            { loading ? <Skeleton active paragraph={ { rows: 1, width: '100%' } } /> :
                                <>
                                    <div className="data-column">
                                        <span className="data">
                                            <span className="highlight major">{ formatNumber( stats.curriculumsCompleted ) }</span>
                                            <p>/{ formatNumber( stats.curriculumsTotal ) }</p>
                                        </span>
                                        <p className="data-subtext tertiary-text">{ getString( 'userSummary.curriculums.completed' ) }</p>
                                    </div>
                                    <div className="data-column">
                                        <span className="data">
                                            <span className="highlight major">{ Math.trunc( stats.curriculumsCompleted / stats.curriculumsTotal * 100 ) }%</span>
                                        </span>
                                        <p className="data-subtext">{ getString( 'userSummary.curriculums.percent' ) }</p>
                                    </div>
                                </>
                            }
                        </div>
                    }
                    { ( loading || stats?.coursesTotal > 0 ) && 
                        <div className={ classNames( 'lesson-data', 'data-item', 'large', { loading } ) }>

                            { loading ? <Skeleton active paragraph={ { rows: 1, width: '100%' } } /> :
                                <>
                                    <div className="data-column">
                                        <span className="data">
                                            <span className="major highlight">{ formatNumber( stats.coursesCompleted ) }</span>
                                            <p>/{ formatNumber( stats.coursesTotal ) }</p>
                                        </span>
                                            
                                        <p className="data-subtext">{ getString( 'userSummary.courses.completed' ) }</p>
                                    </div>
                                    <div className="data-column">
                                        <span className='data'>
                                            <span className="highlight major">{  Math.trunc( stats.coursesCompleted / stats.coursesTotal * 100 ) }%</span>
                                        </span>
                                        <p className="data-subtext">{ getString( 'userSummary.courses.percent' ) }</p>
                                    </div>
                                </>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default UserSummary;
